import React, { FormEvent } from 'react';
import { compose, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import StyledFastSearchContainer from './styles';
import SearchSettings from 'pureUi/SearchSettings';
import { RangeValueType } from 'pureUi/RangeInput';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Link } from 'ui/Link';
import { Heading2 } from 'styles';
import { PrimaryButton } from 'pureUi/Buttons';
import SearchResultList from 'pureUi/SearchResultsList';
import { IWithTravelAgentsDataProps, withTravelAgentsData } from 'hoc/WithTravelAgentsData';
import { clearBookingBuilderAction } from 'store/modules/bookingBuilder';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';

import * as FastSearchActions from 'store/modules/fastSearch/actions';
import * as FastSearchSelectors from 'store/modules/fastSearch/selectors';

import { isInternalUser } from 'store/modules/auth';
import { selectedTaSelector } from 'store/modules/agents/selectors';
import SingleSelect from 'ui/SingleSelect';
import { ESortOrder } from 'store/common/types';
import { ICompanyMembership } from 'services/BookingManagerApi/types/CompanyMemberships';
import { SvgIcon } from 'ui/SvgIcon';
import ChevronLeftIcon from 'ui/Icons/tabler/chevron-left.component.svg';
import { isEqual } from 'lodash-es';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import * as AgentsActions from 'store/modules/agents/actions';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import { validateBookingBuilderParams, parseBookingBuilderQueryParams } from 'containers/HotelContainer/urlParamsUtils';
import { isBlank } from 'utils';
import { withBookingBuilderQueryParamValidation } from 'hoc/WithQueryParamValidation';
import { SearchDrawer } from './SearchDrawer';
import FluidButton from 'ui/FluidButton';
import { CurrencySelector } from './CurrencySelector';
import SearchBarComponent from 'containers/SearchBar/SearchBar';
import { StickyTopBar } from 'containers/HotelContainer/StickyTopBar';

const SearchBarStickyWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentWidth } = useResponsive();
  if (currentWidth <= 1024) {
    return <>{children}</>;
  }
  return <StickyTopBar className="py-[10px]" stickyClassName="px-[60px]">{children}</StickyTopBar>;
};

const SearchBar = SearchBarComponent as React.ComponentType<{ onSearch?: () => void; isSearchIconVisible?: boolean; className?: string }>;
import { IAvailableCurrency } from 'services/BackendApi/types/AvailableCurrenciesResponse';
import { MealPlanNames, StarRating, Filters, HotelResult, SearchOptions, IFiltersCategory, IFilterOption } from 'services/BackendApi';
import { trackSearchOutcome } from 'integrations/trackingEvents';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

const searchResultsSortOptions = [
  { label: 'Ascending', value: ESortOrder.ASC },
  { label: 'Descending', value: ESortOrder.DESC },
];

const deriveSidebarOptionsFromResults = (results: HotelResult[], baseOptions: SearchOptions) => {
  if (!baseOptions) return baseOptions;
  if (!Array.isArray(results) || results.length === 0) return baseOptions;

  const regionSet = new Set<string>();
  const starSet = new Set<string>();
  const categoryMap: { [cat: string]: Set<string> } = {};

  results.forEach(hotel => {
    if (hotel && hotel.region) regionSet.add(hotel.region);
    if (hotel && hotel.starRating) starSet.add(hotel.starRating);
    const hotelFilters = (hotel && hotel.filters) || [];
    hotelFilters.forEach((filter) => {
      if (!filter || !filter.category || !filter.name) return;
      if (!categoryMap[filter.category]) categoryMap[filter.category] = new Set();
      categoryMap[filter.category].add(filter.name);
    });
  });

  const matchCategory = (cat: IFiltersCategory): Set<string> => {
    const names = new Set<string>();
    const keys = Object.keys(categoryMap);
    const title = (cat.categoryTitle || '').toLowerCase();
    const key = (cat.categoryKey || '').toLowerCase();
    keys.forEach(k => {
      const lk = k.toLowerCase();
      if (lk === title || lk === key) {
        categoryMap[k].forEach(n => names.add(n));
      }
    });
    return names;
  };

  const baseRegions = baseOptions.regions || [];
  const regions = [
    ...baseRegions.filter((r: string) => regionSet.has(r)),
    ...Array.from(regionSet).filter((r) => !baseRegions.includes(r)),
  ];

  const baseStarRatings = (baseOptions.starRatings || []) as StarRating[];
  const starRatings: StarRating[] = [
    ...baseStarRatings.filter((s) => starSet.has(s as string)),
    ...(Array.from(starSet).filter((s) => !(baseStarRatings as string[]).includes(s)) as StarRating[]),
  ];

  const knownCategoryKeys = new Set<string>();
  (baseOptions.filtersCategories || []).forEach((c) => {
    if (c.categoryTitle) knownCategoryKeys.add(c.categoryTitle.toLowerCase());
    if (c.categoryKey) knownCategoryKeys.add(c.categoryKey.toLowerCase());
  });

  const fromBase = (baseOptions.filtersCategories || [])
    .map((cat) => {
      const allowed = matchCategory(cat);
      if (allowed.size === 0) return null;
      const existing = (cat.filters || []).filter(
        (f) => allowed.has(f.filterEnumName) || allowed.has(f.filterTitle)
      );
      const existingNames = new Set(
        existing.flatMap((f) => [f.filterEnumName, f.filterTitle].filter(Boolean) as string[])
      );
      const added: IFilterOption[] = Array.from(allowed)
        .filter((name) => !existingNames.has(name))
        .map((name) => ({ filterEnumName: name as Filters, filterTitle: name, filterEnumKey: name }));
      const filters = [...existing, ...added];
      if (filters.length === 0) return null;
      return { ...cat, filters };
    })
    .filter((cat): cat is IFiltersCategory => cat !== null);

  const extraCategories: IFiltersCategory[] = Object.keys(categoryMap)
    .filter((k) => !knownCategoryKeys.has(k.toLowerCase()))
    .map((k) => ({
      categoryTitle: k,
      categoryKey: k,
      filters: Array.from(categoryMap[k]).map((name) => ({
        filterEnumName: name as Filters,
        filterTitle: name,
        filterEnumKey: name,
      })),
    }));

  const filtersCategories = [...fromBase, ...extraCategories];

  return {
    ...baseOptions,
    regions,
    starRatings,
    filtersCategories,
  };
};

export class FastSearchContainer extends React.PureComponent<
  FastSearchProps,
  {
    membershipInModal: ICompanyMembership | null;
    isDrawerOpen: boolean;
    isLodgingsDrawerOpen: boolean;
    isDateDrawerOpen: boolean;
    selectedCurrencyData: IAvailableCurrency | null;
    keepSidebarVisible: boolean;
    cachedSidebarOptions: SearchOptions | null;
    shouldRefreshSidebarOptions: boolean;
  }
> {
  constructor(props) {
    super(props);
    this.state = {
      membershipInModal: null,
      isDrawerOpen: false,
      isLodgingsDrawerOpen: false,
      isDateDrawerOpen: false,
      selectedCurrencyData: null,
      keepSidebarVisible: false,
      cachedSidebarOptions: null,
      shouldRefreshSidebarOptions: true,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    if (!this.props.searchOptions) {
      this.props.getOptions();
    }

    let searchInitiated = false;
    // Automatically execute the URL search query
    if (window.location.search && (!this.props.searchResults || this.props.searchResults.length <= 0)) {
      this.props.initializeQuery(window.location.search.replace('?', ''));
      searchInitiated = true;
    }

    if (searchInitiated) {
      // all other cases initiate search, so avoid double fetches during mount.
      return;
    } else {
      if (!window.location.search && !this.props.searchResults) {
        this.fetchAvailableOffers(this.props.searchQuery);
        clearBookingBuilderAction();
      }

      // If the user has search results, then navigates back to the homepage,
      // edits the query, and hit's search again, trigger a search
      if (this.props.queryHasChanged) {
        this.fetchAvailableOffers(this.props.searchQuery);
        clearBookingBuilderAction();
      }
    }

    // similar to what we do in src/pages/Dashboard/Dashboard.jsx - if we have a selected company,
    // fetch the memberships, so that if a user changes TC inside the booking builder, if they come back
    // it'll be correct
    if (this.props.selectedCompany?.uuid) {
      this.props.getMembershipsRequestAction(this.props.selectedCompany.uuid, false, false);
    }
  }

  componentDidUpdate(prevProps) {
    const dynamicParameters = ParameterService.getParameters();
    const prevCompanyCode = prevProps.selectedCompany ? prevProps.selectedCompany.countryCode : undefined;
    const newCompanyCode = this.props.selectedCompany ? this.props.selectedCompany.countryCode : undefined;

    if (newCompanyCode && newCompanyCode !== prevCompanyCode) {
      this.fetchAvailableOffers(this.props.searchQuery);
    }

    const prevUuidsAll = prevProps.searchResults ? prevProps.searchResults.map(r => r.uuid) : [];
    const currUuidsAll = this.props.searchResults ? this.props.searchResults.map(r => r.uuid) : [];
    if (
      !isEqual(prevUuidsAll, currUuidsAll) &&
      this.state.shouldRefreshSidebarOptions &&
      this.props.searchOptions &&
      this.props.searchResults &&
      this.props.searchResults.length > 0
    ) {
      const cached = deriveSidebarOptionsFromResults(this.props.searchResults, this.props.searchOptions);
      this.setState({ cachedSidebarOptions: cached, shouldRefreshSidebarOptions: false });
    }

    // OWA-5182
    if (dynamicParameters.WOOPRA_DOMAIN && this.props.searchResults) {
      const prevUuids = prevProps.searchResults ? prevProps.searchResults.map(r => r.uuid) : [];
      const uuids = this.props.searchResults.map(r => r.uuid);
      if (!isEqual(prevUuids, uuids)) {
        // Avoid action track for every update, only when results are different
        // Yes, top10 might be the same, but it's a different result
        const top10 = uuids.slice(0, 10);
        // @ts-ignore
        window.woopra &&
          // @ts-ignore
          window.woopra.track('search-outcome', {
            total: uuids.length,
            top10: top10.join(','),
          });
      }
    }
    if(this.props.searchResults && !this.props.searchPending) {
      const prevUuids = prevProps.searchResults ? prevProps.searchResults.map((r: HotelResult) => r.uuid) : [];
      const uuids = this.props.searchResults.map(r => r.uuid); 
      if (!isEqual(prevUuids, uuids)) {
        // Avoid action track for every update, only when results are different
        // Yes, top10 might be the same, but it's a different result
        const top10 = uuids.slice(0, 10);
        trackSearchOutcome({ top10, total: uuids.length });
      }
    }
  }

  // ----------------------

  fetchAvailableOffers(query) {
    const { getOffers } = this.props;
    getOffers(query);
  }

  handleDestinationChange = (e: FormEvent<HTMLInputElement>) => {
    this.props.destinationChange(e.currentTarget.value);
  };

  handleSubmit = () => {
    this.setState({ keepSidebarVisible: false });
    this.fetchAvailableOffers(this.props.searchQuery);
    this.props.updateQueryString();
    clearBookingBuilderAction();

    if (this.state.isDrawerOpen) {
      this.closeDrawer();
      window.scrollTo(0, 0);
    }
  };

  handleToggleLodgingControls = () => {
    this.props.toggleLodgingControls();
  };

  handleSetLogdingControlsVisibility = (visible: boolean) => () => {
    this.props.setLodgingControlsVisibility(visible);
  };

  handleShowNameSearchDropDown = (visible: boolean) => () => {
    this.props.setNamesSearchResultsVisibility(visible);
  };

  handleShowTravelAgentsDropDown = (visible: boolean) => () => {
    this.props.showTaDropdownChange(visible);
  };

  handleShowCompaniesDropDown = (visible: boolean) => () => {
    this.props.showCompanyDropdownChange(visible);
  };

  closeMembershipModal = () => {
    this.setState({ membershipInModal: null });
  };

  openDrawer = () => {
    this.setState({ isDrawerOpen: true });
  };

  closeDrawer = () => {
    this.setState({ isDrawerOpen: false });
  };

  openLodgingsDrawer = () => {
    this.setState({ isLodgingsDrawerOpen: true });
  };

  closeLodgingsDrawer = () => {
    this.setState({ isLodgingsDrawerOpen: false });
  };

  openDateDrawer = () => {
    this.setState({ isDateDrawerOpen: true });
  };

  closeDateDrawer = () => {
    this.setState({ isDateDrawerOpen: false });
  };

  // ---------------------------------------------------

  handleRemoveAllFilters = () => {
    this.props.setAllFilters(false);
  };

  handleResetFilters = () => {
    this.props.resetSidebarFilters();
    this.setState({ keepSidebarVisible: true });
  };

  handleSearchBarSubmit = () => {
    this.setState({ keepSidebarVisible: false, shouldRefreshSidebarOptions: true });
    this.props.resetSidebarFilters();
    const resetQuery = {
      ...this.props.searchQuery,
      lodgings: this.props.searchQuery.lodgings.map(({ numberOfAdults, agesOfAllChildren, repeatCustomer }) => ({
        numberOfAdults,
        agesOfAllChildren: agesOfAllChildren || [],
        repeatCustomer,
      })),
      regions: [] as string[],
      mealPlanCategories: [MealPlanNames.ANY],
      starRatings: [] as StarRating[],
      filters: [] as Filters[],
      priceRange: { min: undefined, max: undefined },
    };
    this.props.getOffers(resetQuery);
    this.props.updateQueryString();
    clearBookingBuilderAction();
    if (this.state.isDrawerOpen) {
      this.closeDrawer();
      window.scrollTo(0, 0);
    }
  };

  handlePriceRangeChange = (type: RangeValueType, value: string) => {
    const action = type === 'min' ? this.props.minPriceChange : this.props.maxPriceChange;

    if (!isNaN(parseInt(value))) {
      action(parseInt(value, 10));
    }

    if (isNaN(parseInt(value))) {
      action(undefined);
    }
  };

  canSearch = () => (this.props.isSr ? this.props.canSrSearch && this.props.canSearch : this.props.canSearch);

  handleSearchResultsSortingChange = (newSortOrder: ESortOrder) => {
    this.props.setSortOrder(newSortOrder);
  };

  handleCurrencyDataChange = (currencyData: IAvailableCurrency | null) => {
    this.setState({ selectedCurrencyData: currencyData });
  };

  renderSideBar = () => {
    return (
      <div className="sidebar">
        {this.props.searchOptions && (
          <SearchSettings
            options={this.state.cachedSidebarOptions || this.props.searchOptions}
            query={this.props.searchQuery}
            showRegions={this.props.showRegions}
            onFilterChange={this.props.toggleFilter}
            onStarRatingChange={this.props.toggleStarRating}
            onOccasionChange={this.props.toggleOccasion}
            onMealPlanChange={this.props.selectMealPlan}
            onToggleMealPlan={this.props.toggleMealPlan}
            onToggleShowRegions={this.props.toggleShowRegions}
            onRegionChange={this.props.toggleRegion}
            canSubmit={this.props.searchPending}
            onPriceRangeChange={this.handlePriceRangeChange}
            onSubmit={this.handleSubmit}
          />
        )}
        <div className="sidebar-group mb-1rem">
          <PrimaryButton className="w-full" disabled={!this.canSearch()} onClick={this.handleSubmit}>
            Apply Filters
          </PrimaryButton>
        </div>
        <div className="sidebar-group mb-1rem">
          <button
            className="w-full py-[10px] border border-brown-100 text-brown-100 bg-white font-hurmegeometric-sans font-bold text-[12px] uppercase tracking-wider cursor-pointer hover:bg-brown-20 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={this.handleResetFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>
    );
  };

  renderResults = () => {
    if (this.props.searchPending) {
      return null;
    }

    if (this.props.offersRequestError) {
      // @ts-ignore
      const errorStatusCode = this.props.offersRequestError?.response?.data?.errors[0].status || null;

      if (errorStatusCode) {
        return (
          <h3 className="search-error-message">
            There was an error with the search, please try again or check your search options ({errorStatusCode})
          </h3>
        );
      } else {
        return (
          <h3 className="search-error-message">
            There was an error with the search, please try again or check your search options
          </h3>
        );
      }
    } else if (this.props.searchResults === null || this.props.searchResults.length <= 0) {
      return (
        <div className="mt-[8px]">
          <p className="m-0 font-hurmegeometric-sans text-[14px] text-black mt-[20px]">No Results Found.</p>
          <p className="m-0 font-hurmegeometric-sans text-[14px] text-black mt-[18px]">
            {this.props.hasSidebarFiltersActive
              ? 'Please, modify Filters and search again.'
              : 'Please, modify your Search.'}
          </p>
        </div>
      );
    }

    return (
      <SearchResultList
        searchResults={this.props.searchResults}
        expandedHighlights={this.props.expandedHighlights}
        selectedTa={this.props.selectedTa}
        selectedCompanyMembership={this.props.selectedCompanyMembership}
        onToggleHighlights={this.props.toggleHighlights}
        selectedCurrencyData={this.state.selectedCurrencyData}
      />
    );
  };

  render() {
    if (!this.props.searchOptions) {
      return null;
    }

    const hasResults = !!this.props.searchResults && this.props.searchResults.length > 0;
    const searchResultsLength = hasResults ? this.props.searchResults!.length : 0;
    const showSidebar = hasResults || this.props.hasSidebarFiltersActive || this.state.keepSidebarVisible;

    const homepage = '/filters';

    return (
      <StyledFastSearchContainer>
        <Link to={homepage} className="backButton flex" internalClassName="flex items-center">
          <span className="w-18px h-18px bg-gray-20 mr-2 inline-flex items-center justify-center">
            <SvgIcon
              IconComponent={ChevronLeftIcon}
              className="w-3 fill-transparent stroke-brown-80 stroke-2 inline-block"
            />
          </span>
          <span className="text-brown-100 text-[12px] font-normal leading-[19px] tracking-wider cursor-pointer font-hurmegeometric-sans select-none">Back to Home</span>
        </Link>
        <div className="searchBarWrapper">
          <SearchBarStickyWrapper>
            <SearchBar onSearch={this.handleSearchBarSubmit} isSearchIconVisible />
          </SearchBarStickyWrapper>
        </div>

        {showSidebar && (
          <div className="drawerTrigger mt-[10px]">
            <FluidButton type="secondary" fixedWidth="119px" onClick={this.openDrawer}>
              Show Filters
            </FluidButton>
          </div>
        )}

        {showSidebar && (
          <div className="sideBar">{this.renderSideBar()}</div>
        )}

        <SearchDrawer isOpen={this.state.isDrawerOpen} onClose={this.closeDrawer}>
          {this.renderSideBar()}
        </SearchDrawer>

        <div className={`searchResults${!showSidebar ? ' searchResults--full-width' : ''} mt-[10px]`}>
          {this.props.searchPending && <Heading2 className="mt-0 font-normal">Loading...</Heading2>}
          {!this.props.searchPending && (
            <div className="search-results-heading-container flex justify-between items-center mb-[16px]">
              <Heading2 className="search-results-heading m-0 text-[26px] font-normal">Search Results {searchResultsLength}</Heading2>
              {hasResults && (
                <div className="flex items-center gap-[10px]">
                  {ParameterService.getParameters().ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP && (
                    <CurrencySelector onCurrencyDataChange={this.handleCurrencyDataChange} />
                  )}
                  <SingleSelect
                    fieldId="search-results-sorting-dropdown"
                    className="search-results-sorting-dropdown w-150px"
                    value={this.props.searchResultsSortOrder}
                    options={searchResultsSortOptions}
                    onChange={this.handleSearchResultsSortingChange}
                  />
                </div>
              )}
            </div>
          )}
          <div className="resultsGrid">{this.renderResults()}</div>
        </div>
      </StyledFastSearchContainer>
    );
  }
}

// -----------------------------------------------------------------------------
// Prop Typings
// -----------------------------------------------------------------------------
export type StateToProps = ReturnType<typeof mapStateToProps>;
export type DispatchToProps = typeof actionCreators;
export interface FastSearchProps
  extends StateToProps,
    DispatchToProps,
    RouteComponentProps,
    IWithTravelAgentsDataProps {
  className: string;
}

const mapStateToProps = createStructuredSelector({
  optionsRequestPending: FastSearchSelectors.searchOptionsPendingSelector,
  optionsRequestError: FastSearchSelectors.searchOptionsErrorSelector,
  offersRequestError: FastSearchSelectors.searchOffersRequestErrorSelector,
  searchOptions: FastSearchSelectors.searchOptionsSelector,
  searchPending: FastSearchSelectors.offersSearchPendingSelector,
  searchResults: FastSearchSelectors.orderedSearchResults,
  searchQuery: FastSearchSelectors.offersQuerySelector,
  originalSearchQuery: FastSearchSelectors.offersOriginalQuerySelector,
  activeFilters: FastSearchSelectors.activeFiltersSelector,
  priceRange: FastSearchSelectors.priceRangeSelector,
  showRegions: FastSearchSelectors.showRegionsSelector,
  activeLodingIndex: FastSearchSelectors.activeLodingIndexSelector,
  expandedHighlights: FastSearchSelectors.expandedHighlightsSelector,
  totalGuestCount: FastSearchSelectors.totalGuestCountSelector,
  showLodgingControls: FastSearchSelectors.showLodgingControlsSelector,
  nameSearchResults: FastSearchSelectors.nameSearchResultsSelector,
  nameSearchResultsRaw: FastSearchSelectors.nameSearchResultsRawSelector,
  nameSearchPending: FastSearchSelectors.nameSearchPendingSelector,
  showNameSearchResults: FastSearchSelectors.showNameSearchResultsSelector,
  selectedDates: FastSearchSelectors.selectedDatesSelector,
  isRepeatGuest: FastSearchSelectors.isRepeatGuestSelector,
  queryHasChanged: FastSearchSelectors.queryHasChangedSelector,
  canSearch: FastSearchSelectors.canSearchSelector,
  getBootstrapCountries: getBootstrapCountriesSelector,
  isSr: isInternalUser,
  selectedTa: selectedTaSelector,
  searchResultsSortOrder: FastSearchSelectors.searchResultsSortOrderSelector,
  selectedCompanyMembership: AgentsSelectors.selectedCompanyMembershipSelector,
  hasSidebarFiltersActive: FastSearchSelectors.hasSidebarFiltersActiveSelector,
});

const actionCreators = {
  getOptions: FastSearchActions.optionsRequestAction,
  getOffers: FastSearchActions.offersSearchRequestAction,
  toggleFilter: FastSearchActions.toggleFilterAction,
  setAllFilters: FastSearchActions.setAllFiltersAction,
  minPriceChange: FastSearchActions.minPriceChangeAction,
  maxPriceChange: FastSearchActions.maxPriceChangeAction,
  toggleStarRating: FastSearchActions.toggleStarRatingAction,
  toggleOccasion: FastSearchActions.toggleOccasionAction,
  selectMealPlan: FastSearchActions.selectMealPlanAction,
  toggleMealPlan: FastSearchActions.toggleMealPlanAction,
  resetSidebarFilters: FastSearchActions.resetSidebarFiltersAction,
  toggleShowRegions: FastSearchActions.toggleShowRegionsAction,
  toggleRegion: FastSearchActions.toggleRegionAction,
  destinationChange: FastSearchActions.destinationChangeAction,
  incrementRoom: FastSearchActions.incrementRoomAction,
  incrementAdult: FastSearchActions.incrementAdultAction,
  incrementChild: FastSearchActions.incrementChildAction,
  setAge: FastSearchActions.setAgeAction,
  setActiveLodgingIndex: FastSearchActions.setActiveLodgingIndexAction,
  toggleHighlights: FastSearchActions.toggleHighlightsAction,
  incrementActiveLodgingIndex: FastSearchActions.incrementActiveLodgingIndexAction,
  toggleLodgingControls: FastSearchActions.toggleLodgingControlsAction,
  setLodgingControlsVisibility: FastSearchActions.setLodgingControlsVisibilityAction,
  setNamesSearchResultsVisibility: FastSearchActions.setNamesSearchResultsVisibilityAction,
  initializeQuery: FastSearchActions.initializeQueryAction,
  toggleRepeatGuest: FastSearchActions.toggleRepeatGuestAction,
  clearBookingBuilderAction,

  dateRangeChangeAction: FastSearchActions.dateRangeChangeAction,
  updateQueryString: FastSearchActions.updateQueryStringAction,
  guestCountryChangeAction: FastSearchActions.clientCountryCodeChangeAction,
  setSortOrder: FastSearchActions.setSortOrderAction,
  getMembershipsRequestAction: AgentsActions.getMembershipsRequestAction,
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(actionCreators, dispatch);

// -----------------------------------------------------------------------------
// Connected
// -----------------------------------------------------------------------------
const withConnect = connect<StateToProps, DispatchToProps, FastSearchProps>(mapStateToProps, mapDispatchToProps);

export const FastSearchContainerConnected = compose(
  withConnect,
  withRouter,
  withTravelAgentsData(),
  withBookingBuilderQueryParamValidation
)(FastSearchContainer);
